import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, User, LogOut, Search, Menu, X, Heart, ShieldCheck, Truck, BadgeCheck, ArrowRight, ChevronDown, Gift } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePlatform } from "../context/PlatformContext";
import NotificationBell from "./NotificationBell";
import PreviewModeBanner from "./PreviewModeBanner";
import ContactAdminWidget from "./ContactAdminWidget";
import { api } from "../lib/apiClient";

// v3.99d.284 — Lightweight cart-item-count hook. The app does not have a
// global cart context (each page fetches /cart independently), so this
// hook polls on mount + on every route change. Good enough to keep the
// header badge accurate as the shopper moves between PDP → cart → home
// without paying the cost of a full context refactor.
function useCartCount() {
  const [count, setCount] = useState(0);
  const { user } = useAuth();
  const loc = useLocation();
  useEffect(() => {
    // Anonymous shoppers can still have a cart (server-side keyed by
    // session cookie), so we don't gate on `user`.
    let cancelled = false;
    api.get("/cart")
      .then(r => {
        if (cancelled) return;
        const items = r.data?.items || [];
        const n = items.reduce((s, it) => s + (it.qty || it.quantity || 1), 0);
        setCount(n);
      })
      .catch(() => { if (!cancelled) setCount(0); });
    return () => { cancelled = true; };
  }, [loc.pathname, user?.id]);
  return count;
}

function MobileCartButton() {
  const count = useCartCount();
  return (
    <Link
      to="/cart"
      className="md:hidden ml-auto relative p-2 hover:opacity-70"
      data-testid="nav-cart-mobile"
      title="Cart"
      aria-label={count > 0 ? `View cart (${count} items)` : "View cart"}
    >
      <ShoppingCart className="w-5 h-5" />
      {count > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center text-[9px] font-mono font-bold"
          style={{
            background: "var(--gold)",
            color: "var(--bg)",
            borderRadius: 9999,
            lineHeight: 1,
          }}
          data-testid="nav-cart-mobile-badge"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}

function AgeGate() {
  const [open, setOpen] = useState(false);
  const { bank_approval_mode, eval_mode } = usePlatform();
  useEffect(() => {
    // v3.99ba — Field Access evaluators skip the age modal entirely so
    // AI agents / SEO crawlers / investors get a frictionless first
    // paint. Backed by a per-tab sessionStorage flag — never persisted
    // across sessions.
    if (eval_mode) { setOpen(false); return; }
    const ok = localStorage.getItem("age_verified");
    if (!ok) setOpen(true);
  }, [eval_mode]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center p-6"
         data-testid="age-gate-modal">
      <div className="bg-[var(--surface)] border border-[var(--border-strong)] max-w-md w-full p-8 fade-up relative" style={{borderRadius:2}}>
        <div className="absolute top-0 left-0 right-0 gold-line"/>
        <div
          className="text-center mb-5 font-display tracking-tight select-none"
          style={{ color: "var(--text)", fontSize: "2rem", lineHeight: 1 }}
          data-testid="age-gate-wordmark"
        >
          Canna<span style={{ color: "var(--primary-text)" }}>Forge</span>
        </div>
        <div className="label-eyebrow mb-3 text-center">Age Verification · Compliance</div>
        <h2 className="font-display text-3xl mb-3 text-center" style={{color:"var(--text)"}}>Are you 21 or older?</h2>
        <p className="text-sm leading-relaxed text-center" style={{color:"var(--text-2)"}}>
          {bank_approval_mode
            ? "CannaForge is a curated cannabis genetics catalog featuring approved breeder brands. Product listings, customer support, and fulfillment are handled through CannaForge. By entering, you confirm you are of legal age in your jurisdiction. Seeds are sold for collection where germination is restricted by local law."
            : "CannaForge is a curated, hand-vetted cannabis genetics platform — verified breeders, managed onboarding, and platform-supported fulfillment. By entering, you confirm you are of legal age in your jurisdiction. Seeds are sold for collection where germination is restricted by local law."}
        </p>
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => { localStorage.setItem("age_verified", "1"); setOpen(false); }}
            className="btn-primary flex-1 justify-center"
            data-testid="age-gate-confirm">I am 21+ — Enter</button>
          <a href="https://www.google.com" className="btn-outline flex-1 text-center"
             data-testid="age-gate-leave">Leave</a>
        </div>
      </div>
    </div>
  );
}

// v3.99bw — Pillar-based navigation. Replaces the flat "8 primary + 8 more"
// header with 4 grouped pillars (Discover · Genetics · Community · For
// Breeders). Each pillar is a hover/click dropdown on desktop and a
// collapsible group on mobile. Per-item flags:
//   tag = "auction" | "breeder-only" | "collector-only" | "hidden"
//   bam = "bam-hide" | "bam-relabel:X"
// Tuple shape: [label, href, testid, tag?, bam?]
const PILLAR_NAVS = [
  {
    key: "discover",
    label: "Discover",
    testid: "nav-pillar-discover",
    items: [
      // v3.99d.286 (Phase 1A) — Lead with the proven engagement engine.
      // Auctions + Giveaways promoted to top of pillar. Removed:
      // Featured Breeders (duplicate of Genetics→Breeders), Vault
      // Releases (folded into New Drops).
      // v3.99d.290 — `Trending` restored at user request: high-traffic
      // discovery surface, deserves its own nav slot.
      ["Auctions",           "/auctions",    "nav-auctions",          "auction"],
      ["Giveaways",          "/giveaways",   "nav-giveaways"],
      ["New Drops",          "/new-drops",   "nav-new-drops"],
      ["Trending",           "/trending",    "nav-trending"],
      ["Shop All Genetics",  "/shop",        "nav-shop-all"],
    ],
  },
  {
    key: "genetics",
    label: "Genetics",
    testid: "nav-pillar-genetics",
    items: [
      // v3.99d.286 — Removed Preservation (now a footer/`/vault`
      // landing entry only — was duplicated under "For Breeders" too).
      ["Strain Atlas",         "/lineage",         "nav-strain-atlas"],
      ["Families",             "/families",        "nav-families"],
      ["Breeders",             "/breeders",        "nav-breeders",        null, "bam-relabel:Brands"],
      ["Genetics Guide",       "/genetics-guide",  "nav-genetics-guide"],
    ],
  },
  {
    key: "community",
    label: "Community",
    testid: "nav-pillar-community",
    items: [
      // v3.99d.286 — Personal tools (My Collection) moved to the
      // logged-in account pill. Community Hub folded into Forge Feed.
      // v3.99d.290 — `Grow Logs` restored at user request: this is the
      // long-term retention surface and a public-facing discovery
      // page in its own right. It still appears on the logged-in
      // dashboard as a dedicated tab.
      ["Forge Feed",          "/forge-feed",        "nav-forge-feed"],
      ["Grow Logs",           "/grow-logs",         "nav-grow-logs"],
      ["Grow-Offs",           "/grow-offs",         "nav-grow-offs"],
      ["Trade Desk",          "/trade",             "nav-trade"],
      ["Grower Resources",    "/grower-resources",  "nav-grow-advice"],
    ],
  },
  {
    key: "for-breeders",
    label: "For Breeders",
    testid: "nav-pillar-breeders",
    items: [
      // v3.99d.286 — Removed Preservation Services (defer to /vault
      // landing). Result: 6 items → 4.
      ["Claim Free Breeder Profile", "/join/breeder",     "nav-apply"],
      ["Breeder Platform",      "/for-breeders",     "nav-breeder-platform"],
      ["Breeder Pricing",       "/breeder-pricing",  "nav-breeder-pricing"],
      ["Breeder Launch Support", "/breeder-services", "nav-managed-fulfillment", null, "bam-hide"],
    ],
  },
];

// Legacy exports retained — some consumer tests import these. The values
// are the pre-restructure flat lists, kept here for back-compat only.
const PRIMARY_NAV_ITEMS = [];
const SECONDARY_NAV_ITEMS = [];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { public_auctions_enabled, bank_approval_mode } = usePlatform();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [openPillarKey, setOpenPillarKey] = useState(null);
  const [mobileOpenPillars, setMobileOpenPillars] = useState({});

  const isCollector = user && user.role === "customer";
  const isBreeder   = user && user.role === "vendor";
  const isInternal  = user && (user.role === "admin" || user.role === "vendor");
  const applyBam    = bank_approval_mode && !isInternal;

  const filterAndRelabelItems = (items) => items
    .filter(([,,,tag, bam]) => {
      if (tag === "auction" && !public_auctions_enabled) return false;
      if (tag === "breeder-only" && !isBreeder && !isInternal) return false;
      if (tag === "collector-only" && !isCollector) return false;
      if (tag === "hidden") return false;
      if (applyBam && bam === "bam-hide") return false;
      return true;
    })
    .map(([label, href, tid, tag, bam]) => {
      if (applyBam && typeof bam === "string" && bam.startsWith("bam-relabel:")) {
        return [bam.slice("bam-relabel:".length), href, tid, tag];
      }
      return [label, href, tid, tag];
    });

  const pillars = PILLAR_NAVS
    .map(p => ({ ...p, items: filterAndRelabelItems(p.items) }))
    .filter(p => p.items.length > 0);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/browse?q=${encodeURIComponent(q.trim())}`);
  };

  const toggleMobilePillar = (key) =>
    setMobileOpenPillars((m) => ({ ...m, [key]: !m[key] }));

  return (
    <>
      <AgeGate />
      <PreviewModeBanner />

      {/* Trust strip */}
      <div className="hidden md:block border-b border-[var(--border)] bg-[var(--bg-2)]">
        <div className="max-w-[1400px] mx-auto px-6 py-1.5 flex items-center justify-between text-[11px]" style={{color:"var(--text-muted)"}}>
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5"><BadgeCheck className="w-3 h-3" style={{color:"var(--primary-text)"}}/>{applyBam ? "Curated Genetics Catalog" : "Breeder Community · Verified Tiers"}</span>
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" style={{color:"var(--primary-text)"}}/>{applyBam ? "Approved Breeder Brands" : "Curated · Platform-Managed"}</span>
            <span className="flex items-center gap-1.5"><Truck className="w-3 h-3" style={{color:"var(--primary-text)"}}/>{applyBam ? "Centrally Managed Fulfillment" : "Breeder Launch Support Available"}</span>
          </div>
          <div className="flex items-center gap-3 font-mono">
            <span style={{color:"var(--gold)"}}>21+ ONLY</span>
            <span>·</span>
            <span>thecannaforge.com</span>
          </div>
        </div>
      </div>

      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center gap-6">
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-home-link">
            <span className="font-display text-2xl tracking-tighter" style={{color:"var(--text)"}}>
              Canna<span style={{color:"var(--primary-text)"}}>Forge</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7 text-sm font-semibold ml-6" style={{color:"var(--text-2)"}} data-testid="primary-nav">
            {pillars.map((pillar) => (
              <PillarMenu
                key={pillar.key}
                pillar={pillar}
                isOpen={openPillarKey === pillar.key}
                onOpen={() => setOpenPillarKey(pillar.key)}
                onClose={() => setOpenPillarKey((k) => (k === pillar.key ? null : k))}
              />
            ))}
          </nav>

          <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-sm ml-auto relative" data-testid="search-form">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10" style={{color:"var(--text-muted)"}}/>
            <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search strains, breeders, lineages…"
                   className="input-field pl-10 !py-2.5" data-testid="search-input"/>
          </form>

          <div className="hidden md:flex items-center gap-1 ml-2">
            {user && user.role === "customer" && (
              <Link to="/account/forge-funds"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 mr-1 text-[10px] font-mono uppercase tracking-[0.16em] border transition hover:opacity-80"
                    style={{ color: "var(--gold)",
                              borderColor: "var(--gold)",
                              background: "rgba(212,175,55,0.06)",
                              borderRadius: 2 }}
                    data-testid="nav-refer-chip"
                    title="Give $10, get $20 — refer a collector">
                <Gift className="w-3 h-3"/>
                $10&nbsp;/&nbsp;$20
              </Link>
            )}
            {user && user.role && (
              <Link to="/account?tab=watchlist" className="p-2 hover:opacity-70" data-testid="nav-watchlist" title="Watchlist">
                <Heart className="w-5 h-5"/>
              </Link>
            )}
            <Link to="/cart" className="p-2 hover:opacity-70" data-testid="nav-cart" title="Cart" aria-label="View cart"><ShoppingCart className="w-5 h-5" /></Link>
            {user && user.role && <NotificationBell/>}
            {user && user.role ? (
              <div className="flex items-center gap-2 ml-2">
                {/* v3.66: prominent gold dashboard pill — was a faint outlined "btn-outline" before. */}
                <Link to={user.role === "admin" ? "/admin" : user.role === "vendor" ? "/vendor" : "/account"}
                      className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-mono uppercase tracking-[0.18em] border-2 transition-all hover:scale-[1.02]"
                      style={{
                        color: "var(--gold)",
                        borderColor: "var(--gold)",
                        background: "rgba(212,175,55,0.06)",
                        borderRadius: 2,
                      }}
                      data-testid="nav-dashboard"
                      title={`${user.role === "admin" ? "Admin" : user.role === "vendor" ? "Breeder" : "Account"} dashboard`}>
                  <User className="w-3.5 h-3.5" />
                  <span style={{ color: "var(--text)" }}>{user.name?.split(" ")[0]}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <button onClick={async ()=>{await logout(); nav("/");}} className="p-2 hover:opacity-70" data-testid="nav-logout" title="Sign out" aria-label="Sign out"><LogOut className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="flex items-center gap-2 ml-2">
                <Link to="/login" className="text-sm font-semibold hover:text-[var(--primary-text)]" data-testid="nav-login">Sign in</Link>
                <Link to="/register" className="btn-primary !py-2 !px-4 text-xs" data-testid="nav-register">Join CannaForge</Link>
              </div>
            )}
          </div>
          {/* v3.66: mobile dashboard pill — sits next to hamburger, was previously hidden inside the drawer. */}
          {/* v3.99d.284: mobile cart button — always visible on mobile so shoppers
              don't have to open the drawer to see their cart. */}
          <MobileCartButton />
          {user && user.role && (
            <Link to={user.role === "admin" ? "/admin" : user.role === "vendor" ? "/vendor" : "/account"}
                  className="md:hidden inline-flex items-center gap-1.5 px-3 py-2 border-2 text-[11px] font-mono uppercase tracking-[0.16em]"
                  style={{ color: "var(--gold)", borderColor: "var(--gold)", background: "rgba(212,175,55,0.06)", borderRadius: 2 }}
                  data-testid="nav-dashboard-mobile">
              <User className="w-3.5 h-3.5"/>
              <span style={{ color: "var(--text)" }}>{user.role === "admin" ? "Admin" : user.role === "vendor" ? "Breeder" : "Account"}</span>
              <ArrowRight className="w-3 h-3"/>
            </Link>
          )}
          <button className={`lg:hidden ml-2 p-2`} onClick={()=>setOpen(!open)} data-testid="mobile-menu-toggle"
                  aria-label={open ? "Close menu" : "Open menu"}
                  aria-expanded={open}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-[var(--border)] px-6 py-5 flex flex-col gap-1 bg-[var(--bg)] max-h-[80vh] overflow-y-auto" data-testid="mobile-nav-drawer">
            {pillars.map((pillar) => {
              const expanded = !!mobileOpenPillars[pillar.key];
              return (
                <div key={pillar.key} className="border-b border-[var(--border)] last:border-b-0" data-testid={`mobile-${pillar.testid}`}>
                  <button
                    type="button"
                    onClick={() => toggleMobilePillar(pillar.key)}
                    className="w-full flex items-center justify-between py-3 text-base font-semibold"
                    style={{ color: "var(--text)" }}
                    data-testid={`mobile-nav-pillar-${pillar.key}-toggle`}>
                    <span>{pillar.label}</span>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
                      style={{ color: "var(--text-muted)" }}
                    />
                  </button>
                  {expanded && (
                    <div className="flex flex-col pb-3 pl-3">
                      {pillar.items.map(([label, href, tid]) => (
                        <Link
                          key={`${pillar.key}-${href}-${tid}`}
                          to={href}
                          onClick={()=>setOpen(false)}
                          data-testid={`${tid}-mobile`}
                          className="py-2 text-sm hover:text-[var(--primary-text)] transition"
                          style={{ color: "var(--text-2)" }}>
                          {label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <hr className="border-[var(--border)] my-4"/>
            <Link to="/cart" onClick={()=>setOpen(false)} className="py-2 text-sm" style={{color:"var(--text-2)"}}>Cart</Link>
            {user && user.role ? (
              <>
                <Link to={user.role === "admin" ? "/admin" : user.role === "vendor" ? "/vendor" : "/account"} onClick={()=>setOpen(false)} className="py-2 text-sm" style={{color:"var(--text-2)"}}>Dashboard</Link>
                <button className="text-left py-2 text-sm" style={{color:"var(--text-2)"}} onClick={async ()=>{await logout(); setOpen(false); nav("/");}}>Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={()=>setOpen(false)} className="py-2 text-sm" style={{color:"var(--text-2)"}}>Sign in</Link>
                <Link to="/register" onClick={()=>setOpen(false)} className="py-2 text-sm" style={{color:"var(--primary-text)"}}>Join CannaForge</Link>
              </>
            )}
          </div>
        )}
      </header>

      <main className="min-h-[60vh] overflow-x-hidden">{children}</main>

      <footer className="mt-32 border-t border-[var(--border)] bg-[var(--bg-2)]">
        <div className="max-w-[1400px] mx-auto px-6 py-16 grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2">
            <div className="flex items-center mb-4">
              <span className="font-display text-2xl" style={{color:"var(--text)"}}>
                Canna<span style={{color:"var(--primary-text)"}}>Forge</span>
              </span>
            </div>
            <p className="text-sm max-w-sm leading-relaxed" style={{color:"var(--text-2)"}}>
              {applyBam
                ? "Curated genetics catalog featuring approved breeder brands. Product listings, customer support, and fulfillment handled centrally by CannaForge."
                : "Curated breeder community and premium genetics platform. Hand-vetted breeders, application review, managed fulfillment options, and platform oversight on every drop."}
            </p>
            <div className="mt-5 flex items-center gap-2 text-xs" style={{color:"var(--text-muted)"}}>
              <ShieldCheck className="w-4 h-4" style={{color:"var(--primary-text)"}}/>
              <span>Curated · Platform-Managed · 21+ Only</span>
            </div>
          </div>
          <FooterCol title="The Community" links={[
            ...(applyBam ? [] : [["Community Hub","/community"]]),
            ["Forge Feed","/forge-feed"],
            ["Forge Dispatch","/dispatch"],
            [applyBam ? "Featured Brands" : "Verified Breeders", "/breeders"],
            ...(public_auctions_enabled && !applyBam ? [["Auctions","/auctions"]] : []),
            ["New Drops","/new-drops"],
            ["Shop Genetics","/browse"],
          ]}/>
          {applyBam ? (
            <FooterCol title="The Catalog" links={[
              ["Genetics Catalog","/browse"],
              ["Featured Brands","/breeders"],
              ["Lineage Archive","/lineage"],
              ["Upcoming Releases","/new-drops"],
            ]}/>
          ) : (
            <FooterCol title="For Breeders" links={[["Apply as Breeder","/join/breeder"],["Breeder Launch Support","/breeder-services"],["Breeder Pricing","/breeder-pricing"],["Application Review","/join/breeder"],["Launch Partner Program","/join/breeder"]]}/>
          )}
          <FooterCol title="Legal" links={[["Terms","/terms"],["Privacy","/privacy"],["Compliance","/compliance"],["Shipping","/shipping"],["Contact","/contact"],["FAQ","/faq"]]}/>
        </div>

        {/* v3.99bf — Direct contact strip (merchant-bank compliance: 2 of phone / email / address required). */}
        <div className="border-t border-[var(--border)]">
          <div className="max-w-[1400px] mx-auto px-6 py-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs" style={{color:"var(--text-2)"}} data-testid="footer-contact-strip">
            <div>
              <div className="label-eyebrow mb-2" style={{color:"var(--gold)"}}>Phone</div>
              <a href="tel:+16164017103" className="font-mono hover:text-[var(--primary-text)] transition" data-testid="footer-phone">+1 (616) 401-7103</a>
            </div>
            <div>
              <div className="label-eyebrow mb-2" style={{color:"var(--gold)"}}>Email</div>
              <a href="mailto:support@thecannaforge.com" className="font-mono hover:text-[var(--primary-text)] transition" data-testid="footer-email">support@thecannaforge.com</a>
            </div>
            <div>
              <div className="label-eyebrow mb-2" style={{color:"var(--gold)"}}>Mailing · Breeder consignment</div>
              <address className="font-mono not-italic" data-testid="footer-address">
                CannaForge<br/>
                7125 Headley ST SE<br/>
                #1038<br/>
                Ada, MI 49301
              </address>
            </div>
          </div>
        </div>

        {/* v3.99bf — FDA disclaimer (required on every page by merchant-bank compliance). */}
        <div className="border-t border-[var(--border)] bg-[var(--bg)]">
          <div className="max-w-[1400px] mx-auto px-6 py-5 text-[11px] leading-relaxed" style={{color:"var(--text-muted)"}} data-testid="footer-fda-disclaimer">
            <span className="font-mono uppercase tracking-[0.2em] mr-2" style={{color:"var(--gold)"}}>FDA Disclaimer</span>
            These statements have not been evaluated by the Food and Drug Administration. Products sold on CannaForge are not intended to diagnose, treat, cure, or prevent any disease. Cannabis seeds are sold for souvenir, genetic preservation, and collection purposes only — germinate only where legally permitted. You must be 21 years of age or older to purchase. Buyer is solely responsible for compliance with all federal, state, and local laws.
          </div>
        </div>

        <div className="border-t border-[var(--border)]">
          <div className="max-w-[1400px] mx-auto px-6 py-5 text-xs flex flex-col md:flex-row gap-2 justify-between" style={{color:"var(--text-muted)"}}>
            <span>© {new Date().getFullYear()} CannaForge · thecannaforge.com — {applyBam ? "Curated genetics catalog featuring approved breeder brands · sold for collection / souvenir purposes where germination is illegal." : "Curated genetics platform · sold for collection / souvenir purposes where germination is illegal."}</span>
            <span className="font-mono">21+ · {applyBam ? "Curated Catalog · Approved Brands" : "Verified Breeders · Curated Platform"}</span>
            {applyBam && (
              <a href="/about/catalog" className="hover:opacity-80 transition-opacity underline" data-testid="footer-about-catalog">
                About the catalog
              </a>
            )}
          </div>
        </div>
      </footer>
      <ContactAdminWidget />
    </>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="label-eyebrow mb-4">{title}</div>
      <ul className="space-y-2 text-sm" style={{color:"var(--text-2)"}}>
        {links.map(([n, href], i) => <li key={`${href}-${i}`}><Link to={href} className="hover:text-[var(--primary-text)] transition">{n}</Link></li>)}
      </ul>
    </div>
  );
}

// v3.99bw — PillarMenu: a hover/click dropdown for one nav pillar. Hover
// opens the menu after a tiny delay (intent), click toggles, outside-click
// or Escape closes. Archival styling, hairline border, gold accent stripe.
function PillarMenu({ pillar, isOpen, onOpen, onClose }) {
  const ref = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  const handleEnter = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    onOpen();
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => onClose(), 150);
  };

  return (
    <div
      className="relative"
      ref={ref}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}>
      <button
        type="button"
        onClick={() => (isOpen ? onClose() : onOpen())}
        data-testid={pillar.testid}
        aria-expanded={isOpen}
        className={`inline-flex items-center gap-1 hover:text-[var(--text)] transition ${isOpen ? "text-[var(--text)]" : ""}`}>
        {pillar.label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div
          className="absolute left-0 mt-3 min-w-[240px] bg-[var(--surface)] border border-[var(--border-strong)] shadow-2xl"
          style={{ borderRadius: 2 }}
          data-testid={`${pillar.testid}-dropdown`}>
          <div className="absolute top-0 left-0 right-0 gold-line"/>
          <div className="py-2">
            {pillar.items.map(([label, href, tid]) => (
              <NavLink
                key={`${pillar.key}-${href}-${tid}`}
                to={href}
                onClick={() => onClose()}
                data-testid={tid}
                className={({isActive}) =>
                  `block px-4 py-2 text-sm hover:bg-[var(--bg-2)] hover:text-[var(--text)] transition ${
                    isActive ? "text-[var(--text)]" : "text-[var(--text-2)]"
                  }`}>
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
